import { supabase } from './supabaseClient';

// Shared insert with VISIBLE failures. supabase-js returns { error } rather
// than throwing, so the old per-function try/catches never saw a rejection —
// which is how three event types failed silently for months against the old
// CHECK constraint. Never throws to the user; tracking must not break the app.
async function logEvent(row) {
  try {
    const { error } = await supabase.from('activity_log').insert(row);
    if (error) console.warn(`[activityTracker] ${row.event_type} insert failed:`, error.message);
  } catch (e) {
    console.warn(`[activityTracker] ${row.event_type} insert failed:`, e?.message || e);
  }
}

// Track a sign-in event
export async function trackSignIn(userId) {
  if (!userId) return;
  logEvent({
    user_id: userId,
    event_type: 'sign_in',
    created_at: new Date().toISOString(),
  });
}

// Track session start — stores start time in memory
let sessionStart = null;
let sessionUserId = null;
let sessionFlushTimer = null;
// Idle guard: an open-but-untouched tab must not accrue "active minutes".
// Only intervals with at least one real interaction (and a visible tab) count.
let lastActivity = null;
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'scroll', 'touchstart'];
const MAX_MINUTES_PER_FLUSH = 30;

function markActivity() {
  if (document.visibilityState === 'visible') lastActivity = Date.now();
}

export function trackSessionStart(userId) {
  if (!userId) return;
  // Already tracking this user (e.g. Supabase re-fires SIGNED_IN on token
  // refresh / tab focus) — keep the running timer instead of resetting it.
  if (sessionUserId === userId && sessionFlushTimer) return;

  sessionStart = Date.now();
  sessionUserId = userId;
  lastActivity = Date.now();

  // Flush session time every 5 minutes in case user doesn't explicitly sign out
  if (sessionFlushTimer) clearInterval(sessionFlushTimer);
  sessionFlushTimer = setInterval(() => {
    flushSessionTime(false);
  }, 5 * 60 * 1000);

  ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, markActivity, { passive: true }));
  // Also flush on page unload
  window.addEventListener('beforeunload', handleUnload);
}

export function trackSessionEnd() {
  flushSessionTime(true);
  if (sessionFlushTimer) {
    clearInterval(sessionFlushTimer);
    sessionFlushTimer = null;
  }
  ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, markActivity));
  window.removeEventListener('beforeunload', handleUnload);
  sessionStart = null;
  sessionUserId = null;
}

function handleUnload() {
  flushSessionTime(true);
}

async function flushSessionTime(reset) {
  if (!sessionStart || !sessionUserId) return;

  // No interaction since this interval started → idle tab, count nothing.
  const wasActive = lastActivity && lastActivity >= sessionStart;
  const minutes = Math.min(
    Math.round((Date.now() - sessionStart) / 60000),
    MAX_MINUTES_PER_FLUSH
  );

  if (wasActive && minutes >= 1) {
    logEvent({
      user_id: sessionUserId,
      event_type: 'session_time',
      value: minutes,
      created_at: new Date().toISOString(),
    });
  }

  if (reset) {
    sessionStart = null;
    sessionUserId = null;
  } else {
    // Reset timer for next interval
    sessionStart = Date.now();
  }
}

// Track an image upload
export async function trackImageUpload(userId) {
  if (!userId) return;
  logEvent({
    user_id: userId,
    event_type: 'image_upload',
    created_at: new Date().toISOString(),
  });
}


// Track a Resources page view
export async function trackResourcesView(userId) {
  if (!userId) return;
  logEvent({
    user_id: userId,
    event_type: 'resources_view',
    created_at: new Date().toISOString(),
  });
}


// Track a Resources Timeline page view
export async function trackTimelineView(userId) {
  if (!userId) return;
  logEvent({
    user_id: userId,
    event_type: 'timeline_view',
    created_at: new Date().toISOString(),
  });
}


// Track a Set Summary page view. Called via redirect from a tiny stub
// wrapper page; static .html files don't run our React tracker directly.
// The set id lives in properties — `value` is an integer column, and writing
// the slug into it made every one of these inserts fail on type.
export async function trackSetSummaryView(userId, setId) {
  if (!userId) return;
  logEvent({
    user_id: userId,
    event_type: 'set_summary_view',
    properties: { set_id: setId },
    created_at: new Date().toISOString(),
  });
}


// === CK 3.0 activation funnel =============================================

// Navigation source for the NEXT checklist open ('my_checklists',
// 'empty_state', 'set_summary'). sessionStorage rather than a module variable
// so it survives the signed-out detour: a "Mark what I own" tap navigates to
// /sets/<id>, bounces through the auth wall and an OAuth redirect (full page
// reload), and the checklist then mounts in a fresh JS context.
const OPEN_SOURCE_KEY = 'ck_set_open_source';

export function markSetOpenSource(setId, source) {
  try {
    sessionStorage.setItem(OPEN_SOURCE_KEY, JSON.stringify({ setId, source }));
  } catch {}
}

// Remembered for card_marked events fired later in the same checklist visit.
let lastOpenSource = 'other';
let lastOpenSetId = null;
let lastOpenAt = 0;

// A user opened an interactive checklist (/sets/<id>).
export async function trackSetOpened(userId, setId) {
  // Dedupe double-invoked mount effects (React 18 StrictMode remounts in dev,
  // fast remounts generally): one open per set per 2s window — the first call
  // resolved the real source, a second would log a bogus 'other'.
  const now = Date.now();
  if (lastOpenSetId === setId && now - lastOpenAt < 2000) return;
  lastOpenSetId = setId;
  lastOpenAt = now;
  let source = 'other';
  try {
    const pending = JSON.parse(sessionStorage.getItem(OPEN_SOURCE_KEY) || 'null');
    if (pending && pending.setId === setId) source = pending.source;
    sessionStorage.removeItem(OPEN_SOURCE_KEY);
  } catch {}
  lastOpenSource = source;
  if (!userId) return;
  logEvent({
    user_id: userId,
    event_type: 'set_opened',
    properties: { set_id: setId, source },
    created_at: new Date().toISOString(),
  });
}

// A collection row was created or its status changed. Bulk-mark fires ONE
// event carrying { count } (v_activation_by_week sums it); single marks carry
// { card_id }. Clears/deletes are deliberately not tracked.
export async function trackCardMarked(userId, cardKey, status, count) {
  if (!userId) return;
  const properties = {
    set_id: String(cardKey).split('__')[0],
    status,
    source: lastOpenSource,
  };
  if (count && count > 1) {
    properties.count = count;
  } else {
    properties.card_id = cardKey;
  }
  logEvent({
    user_id: userId,
    event_type: 'card_marked',
    properties,
    created_at: new Date().toISOString(),
  });
}

// The "Mark what I own" button on a Set Summary. Fires for signed-out
// visitors too (user_id null — a dedicated anon RLS policy allows exactly
// this one event type).
export async function trackTrackSetClicked(userId, setId, signedIn) {
  logEvent({
    user_id: userId || null,
    event_type: 'track_set_clicked',
    properties: { set_id: setId, signed_in: !!signedIn },
    created_at: new Date().toISOString(),
  });
}

// The first-set empty state on /. Viewed fires once per session (guarded by
// sessionStorage so refreshes don't inflate it).
const EMPTY_STATE_VIEWED_KEY = 'ck_empty_state_viewed';

export async function trackEmptyStateViewed(userId) {
  if (!userId) return;
  try {
    if (sessionStorage.getItem(EMPTY_STATE_VIEWED_KEY)) return;
    sessionStorage.setItem(EMPTY_STATE_VIEWED_KEY, '1');
  } catch {}
  logEvent({
    user_id: userId,
    event_type: 'empty_state_viewed',
    created_at: new Date().toISOString(),
  });
}

export async function trackEmptyStateSetClicked(userId, setId) {
  if (!userId) return;
  logEvent({
    user_id: userId,
    event_type: 'empty_state_set_clicked',
    properties: { set_id: setId },
    created_at: new Date().toISOString(),
  });
}
